import type { Customer, DuplicateCandidate, LeadForm, OcrProvider, PublicSettings } from './types';

const sessionKey = 'leadflow-access-token';

type SaveMode = 'check' | 'allow';

type SaveCustomerResult = { customer: Customer; message: string };

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly payload: unknown) {
    const message = typeof payload === 'object' && payload && 'message' in payload && typeof (payload as { message?: unknown }).message === 'string'
      ? (payload as { message: string }).message
      : '请求未成功，请稍后重试。';
    super(message);
    this.name = 'ApiError';
  }
}

export function hasSession() { return Boolean(sessionStorage.getItem(sessionKey)); }
export function endSession() { sessionStorage.removeItem(sessionKey); }

function authHeaders(headers?: HeadersInit) {
  const token = sessionStorage.getItem(sessionKey);
  return { ...(headers ?? {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { ...init, headers: authHeaders(init?.headers) });
  const body: unknown = await response.json().catch(() => ({ message: '服务器返回了无效响应。' }));
  if (!response.ok) {
    if (response.status === 401) {
      endSession();
      window.dispatchEvent(new Event('leadflow:unauthorized'));
    }
    throw new ApiError(response.status, body);
  }
  return body as T;
}

export function duplicateConflictOf(cause: unknown): { message: string; duplicates: DuplicateCandidate[] } | null {
  if (!(cause instanceof ApiError) || cause.status !== 409 || typeof cause.payload !== 'object' || !cause.payload) return null;
  const payload = cause.payload as { message?: unknown; duplicates?: unknown };
  if (!Array.isArray(payload.duplicates)) return null;
  return { message: typeof payload.message === 'string' ? payload.message : '发现可能重复的客户记录。', duplicates: payload.duplicates as DuplicateCandidate[] };
}

export function isNetworkUnavailable(cause: unknown) {
  return cause instanceof TypeError || (cause instanceof ApiError && cause.status >= 502);
}

export async function startSession(password: string) {
  const response = await fetch('/api/auth/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) });
  const body = await response.json().catch(() => ({ message: '服务器返回了无效响应。' })) as { token?: string; message?: string };
  if (!response.ok || !body.token) throw new Error(body.message || '无法建立访问会话。');
  sessionStorage.setItem(sessionKey, body.token);
}

export function recognizeCard(file: File) {
  const form = new FormData();
  form.append('image', file);
  return request<{ data: Partial<LeadForm> }>('/api/ocr', { method: 'POST', body: form });
}

export function saveCustomer(data: LeadForm, duplicateMode: SaveMode = 'check') {
  return request<SaveCustomerResult>('/api/customers', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...data, duplicateMode }) });
}

export function listCustomers(query = '') { return request<{ customers: Customer[] }>(`/api/customers?q=${encodeURIComponent(query)}`); }
export function retryCustomer(id: string) { return request<{ customer: Customer; message: string }>(`/api/customers/${id}/retry`, { method: 'POST' }); }
export function getSettings() { return request<PublicSettings>('/api/settings'); }

export interface SettingsPayload {
  appId?: string;
  appSecret?: string;
  appToken?: string;
  tableId?: string;
  ocrProvider?: OcrProvider;
  ocrApiBase?: string;
  ocrApiKey?: string;
  ocrModel?: string;
  tencentSecretId?: string;
  tencentSecretKey?: string;
  tencentRegion?: string;
  webhookUrl?: string;
}

export function saveSettings(data: SettingsPayload) { return request<PublicSettings>('/api/settings', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }); }
export function testLarkConnection() { return request<{ message: string; tableCount: number }>('/api/settings/test-lark', { method: 'POST' }); }

export async function exportCustomers(format: 'csv' | 'xlsx') {
  const response = await fetch(`/api/export?format=${format}`, { headers: authHeaders() });
  if (!response.ok) { const body = await response.json().catch(() => ({ message: '导出失败。' })); throw new Error(body.message || '导出失败。'); }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = `展会客户.${format}`;
  document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
}
